import { intlLocaleFor, formatMoneyCode } from "../../lib/formatMoney";

/**
 * Pure axis and tooltip formatters for the super-admin charts.
 *
 * Series dates are server-side UTC days ("2026-10-07"). `new Date("2026-10-07")` is UTC
 * midnight, so a reader west of UTC would see the 6th with a local formatter: every day
 * format here pins `timeZone: "UTC"`.
 */
const dayParts = (isoDate) => new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);

const dayFormat = (isoDate, lang, options) =>
  new Intl.DateTimeFormat(intlLocaleFor(lang), { timeZone: "UTC", ...options }).format(dayParts(isoDate));

/** "Oct 7" / "7 oct." / "7 oct". */
export const formatDayTick = (isoDate, lang) => dayFormat(isoDate, lang, { month: "short", day: "numeric" });

/** "October 7, 2026" / "7 octobre 2026" / "7 de octubre de 2026". */
export const formatDayLong = (isoDate, lang) =>
  dayFormat(isoDate, lang, { day: "numeric", month: "long", year: "numeric" });

/**
 * The least distance, in px, between two labelled days. Labels are centred on their day,
 * but the last one (today) ends on its day, so the label before it must clear a whole label
 * plus half of its own: the widest short day label of the three languages at the theme's
 * 11px measures about 42px ("10 mars"), and 42 + 21 leaves a 9px gap, room for a wider
 * system font. The same holds at the other end when the first day starts its label.
 */
export const DAY_TICK_SPACING_PX = 72;

/**
 * The days of a dense daily axis that carry a label, on a plot `plotWidth` px wide: every
 * `step` days, anchored on the last day (today), so every gap is the same number of days.
 *
 * `step` is the fewest days that put two labels at least `DAY_TICK_SPACING_PX` apart. A day
 * is at least `plotWidth / n` px wide on every Recharts category axis (a bar chart gives each
 * day exactly that; a line or area chart, `plotWidth / (n - 1)`), so
 * `step = ceil(DAY_TICK_SPACING_PX * n / plotWidth)`. An unmeasured or non-positive width
 * labels today only.
 *
 * The spacing is derived directly, not from a label count spread over the days: a count does
 * not bound the gap (30 days on 300px gave 5 labels 60px apart).
 *
 * Pure on purpose: Recharts' own choice (`interval="preserveEnd"` + `minTickGap`) measures
 * the label text through a hidden DOM node and caches the result for the page's life, so the
 * same data labelled different days on a first load and a reload, with uneven gaps.
 */
export function dayTicks(dates, plotWidth) {
  const n = (dates || []).length;
  if (n === 0) return [];
  const w = Number(plotWidth);
  const step = Number.isFinite(w) && w > 0 ? Math.max(1, Math.ceil((DAY_TICK_SPACING_PX * n) / w)) : n;
  const ticks = [];
  for (let i = n - 1; i >= 0; i -= step) ticks.push(dates[i]);
  return ticks.reverse();
}

/**
 * Axis money: "$12.5K" for 1 250 000 cents. Falls back to the code style when Intl refuses
 * the code; an empty currency gives a bare compact number.
 */
export function formatCompactMoney(cents, currency, lang) {
  const n = Number(cents);
  const amount = Number.isFinite(n) ? n / 100 : 0;
  const c = String(currency || "").trim().toUpperCase();
  const locale = intlLocaleFor(lang);
  const compact = { notation: "compact", maximumFractionDigits: 1 };
  if (!c) return new Intl.NumberFormat(locale, compact).format(amount);
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: c, ...compact }).format(amount);
  } catch {
    return formatMoneyCode(cents, c);
  }
}

/** A count in the language's grouping: "1,234" / "1 234". */
export const formatCount = (n, lang) => {
  const v = Number(n);
  return new Intl.NumberFormat(intlLocaleFor(lang)).format(Number.isFinite(v) ? v : 0);
};
